import React from 'react';
import { TrendingUp, Users, Activity, Trophy, ExternalLink, Timer, CheckCircle } from 'lucide-react';
import '../styles/StravaSegments.css';

/**
 * Component COMPLIANT amb Strava API Brand Guidelines:
 * - Logo "Powered by Strava" obligatori
 * - Links "View on Strava" en anglès
 * - Color taronja #FC5200 per links
 * - Dades d'usuari mostrades directament (no enviades a AI)
 * 
 * Ref: https://developers.strava.com/guidelines
 */

// Logo oficial Strava (SVG inline per evitar dependències externes)
const StravaLogo = ({ size = 24, color = "#FC4C02" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    xmlns="http://www.w3.org/2000/svg"
    aria-label="Strava"
  >
    <path 
      fill={color}
      d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0L0 17.944h6.121"
    />
  </svg>
);

const StravaSegments = ({ segments, isUserConnected = false }) => {
  if (!segments || segments.length === 0) {
    return (
      <div className="strava-segments-container">
        <div className="no-segments">
          <Activity size={48} />
          <p>No s'han trobat segments de Strava a la zona</p>
        </div>
      </div>
    );
  }

  const formatTime = (seconds) => {
    if (!seconds) return null;
    if (seconds < 3600) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('ca-ES', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const getClimbCategory = (category) => {
    const categories = {
      0: { label: 'Pla', color: '#22c55e' },
      1: { label: 'Cat 4', color: '#84cc16' },
      2: { label: 'Cat 3', color: '#eab308' },
      3: { label: 'Cat 2', color: '#f97316' },
      4: { label: 'Cat 1', color: '#ef4444' },
      5: { label: 'HC', color: '#991b1b' },
    };
    return categories[category] || categories[0];
  };

  // Comptar quants segments té l'usuari amb PR
  const userSegmentsCount = segments.filter(s => s.user_pr_time_sec).length;

  return (
    <div className="strava-segments-container fade-in">
      {/* Header amb "Powered by Strava" - OBLIGATORI segons Brand Guidelines */}
      <div className="strava-header" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <StravaLogo size={28} />
          <div>
            <h3 style={{ margin: 0 }}>Segments de Strava</h3>
            <p className="strava-subtitle" style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#6b7280' }}>
              {segments.length} segments trobats a la ruta
              {userSegmentsCount > 0 && (
                <span style={{ color: '#10b981', marginLeft: '8px' }}>
                  • {userSegmentsCount} amb el teu PR
                </span>
              )}
            </p>
          </div>
        </div>
        
        {/* Badge "Powered by Strava" - OBLIGATORI */}
        <a 
          href="https://www.strava.com" 
          target="_blank" 
          rel="noopener noreferrer"
          className="powered-by-strava"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            backgroundColor: '#FC4C02',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '4px',
            fontSize: '0.75rem',
            fontWeight: '600',
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap'
          }}
        >
          <StravaLogo size={14} color="white" />
          <span>Powered by Strava</span>
        </a>
      </div>

      <div className="segments-list">
        {segments.map((segment, index) => {
          const climbInfo = getClimbCategory(segment.climb_category);
          const hasUserPR = segment.user_pr_time_sec != null;

          return (
            <div 
              key={segment.id || index} 
              className={`segment-item ${hasUserPR ? 'has-user-pr' : ''}`}
              style={hasUserPR ? { borderLeft: '4px solid #10b981' } : {}}
            >
              <div className="segment-rank">#{index + 1}</div>

              <div className="segment-content">
                <div className="segment-header-row">
                  <h4>
                    {segment.start_km !== undefined && (
                      <span style={{color: '#666', marginRight: '8px', fontSize: '0.9em'}}>
                        KM {segment.start_km}
                      </span>
                    )}
                    {segment.name || "Segment sense nom"}
                    {segment.direction && (
                      <span className={`segment-direction ${segment.direction === '←' ? 'reverse' : 'forward'}`}>
                        {segment.direction}
                      </span>
                    )}
                  </h4>
                  <span className="climb-category" style={{ backgroundColor: climbInfo.color }}>
                    {climbInfo.label}
                  </span>
                </div>

                <div className="segment-stats">
                  <div className="stat-item">
                    <Activity size={16} />
                    <span>{(segment.distance || 0).toFixed(0)}m</span>
                  </div>

                  <div className="stat-item">
                    <TrendingUp size={16} />
                    <span>{(segment.avg_grade || 0).toFixed(1)}%</span>
                  </div>

                  {segment.kom_time_sec && (
                    <div className="stat-item" title="King of the Mountain">
                      <Trophy size={16} color="#f59e0b" />
                      <span>KOM {formatTime(segment.kom_time_sec)}</span>
                    </div>
                  )}

                  {segment.qom_time_sec && (
                    <div className="stat-item" title="Queen of the Mountain">
                      <Trophy size={16} color="#ec4899" />
                      <span>QOM {formatTime(segment.qom_time_sec)}</span>
                    </div>
                  )}

                  {segment.effort_count != null && (
                    <div className="stat-item">
                      <Users size={16} />
                      <span>{Number(segment.effort_count).toLocaleString()} intents</span>
                    </div>
                  )}
                </div>

                {/* ============================================ */}
                {/* PR DE L'USUARI - NOU! */}
                {/* ============================================ */}
                {hasUserPR && (
                  <div 
                    className="user-pr-section"
                    style={{
                      marginTop: '12px',
                      padding: '10px 14px',
                      backgroundColor: '#ecfdf5',
                      borderRadius: '8px',
                      border: '1px solid #a7f3d0'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      {/* PR Time */}
                      <div 
                        className="user-pr-time"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Timer size={18} color="#059669" />
                        <span style={{ 
                          fontWeight: '700', 
                          fontSize: '1.1rem',
                          color: '#047857'
                        }}>
                          El teu PR: {formatTime(segment.user_pr_time_sec)}
                        </span>
                      </div>

                      {/* Effort count */}
                      {segment.user_effort_count > 0 && (
                        <div 
                          className="user-efforts"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#059669',
                            fontSize: '0.9rem'
                          }}
                        >
                          <CheckCircle size={14} />
                          <span>{segment.user_effort_count}x completat</span>
                        </div>
                      )}

                      {/* PR Date */}
                      {segment.user_pr_date && (
                        <span 
                          className="user-pr-date"
                          style={{
                            fontSize: '0.8rem',
                            color: '#6b7280',
                            marginLeft: 'auto'
                          }}
                        >
                          📅 {formatDate(segment.user_pr_date)}
                        </span>
                      )}
                    </div>

                    {/* Comparativa amb KOM/QOM */}
                    {(segment.kom_time_sec || segment.qom_time_sec) && (
                      <div 
                        className="pr-comparison"
                        style={{
                          marginTop: '8px',
                          paddingTop: '8px',
                          borderTop: '1px dashed #a7f3d0',
                          fontSize: '0.85rem',
                          color: '#6b7280'
                        }}
                      >
                        {segment.kom_time_sec && (
                          <span style={{ marginRight: '16px' }}>
                            vs KOM: +{formatTime(segment.user_pr_time_sec - segment.kom_time_sec)}
                          </span>
                        )}
                        {segment.qom_time_sec && (
                          <span>
                            vs QOM: +{formatTime(segment.user_pr_time_sec - segment.qom_time_sec)}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Link a l'activitat del PR */}
                    {segment.user_pr_activity_id && (
                      <a
                        href={`https://www.strava.com/activities/${segment.user_pr_activity_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          marginTop: '8px',
                          fontSize: '0.8rem',
                          color: '#059669',
                          textDecoration: 'none'
                        }}
                      >
                        <ExternalLink size={12} />
                        Veure activitat del PR
                      </a>
                    )}
                  </div>
                )}

                {/* Coordenades (opcional) */}
                {segment.start_lat && segment.start_lon && (
                  <div className="segment-coordinates">
                    📍 {segment.start_lat.toFixed(5)}, {segment.start_lon.toFixed(5)}
                  </div>
                )}

                {/* Link "View on Strava" - Text OBLIGATORI en anglès segons Brand Guidelines */}
                {segment.id && (
                  <a 
                    href={`https://www.strava.com/segments/${segment.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="strava-view-link"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '10px',
                      padding: '6px 12px',
                      backgroundColor: 'transparent',
                      color: '#FC5200',
                      textDecoration: 'none',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      border: '1px solid #FC5200',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ExternalLink size={14} />
                    View on Strava
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer amb atribució - Recomanat per Brand Guidelines */}
      <div 
        className="strava-footer"
        style={{
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        <StravaLogo size={18} />
        <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
          Segment data from{' '}
          <a 
            href="https://www.strava.com" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#FC5200', fontWeight: '600', textDecoration: 'none' }}
          >
            Strava
          </a>
        </span>
      </div>
    </div>
  );
};

export default StravaSegments;